import { useState, useEffect } from 'react';
import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

export default function InjectJobModal({ onClose, onSuccess }) {
  const [machines, setMachines] = useState([]);
  const [paidQuotes, setPaidQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    slicerQuoteId: '',
    targetMachineId: '',
    sequenceMode: 'BATCH',
    reslice: false,
    instructions: '',
  });
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMachines();
    fetchPaidQuotes();
  }, []);

  const fetchMachines = async () => {
    try {
      const response = await api.get('/auth/slicer-settings/machines');
      setMachines(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch machines:', err);
    }
  };

  const fetchPaidQuotes = async () => {
    setQuotesLoading(true);
    try {
      const response = await api.get('/auth/slicer-quote/board', { params: { statuses: 'PAID' } });
      const data = response.data.data || response.data;
      const board = data.board || {};
      // Flatten all quotes from the board (keyed by status)
      const quotes = Object.values(board).flat();
      setPaidQuotes(quotes);
    } catch (err) {
      console.error('Failed to fetch paid quotes:', err);
    } finally {
      setQuotesLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.slicerQuoteId || !formData.targetMachineId) {
      setError('Slicer Quote ID and Target Machine are required.');
      return;
    }

    setLoading(true);
    try {
      const data = new FormData();
      data.append('slicerQuoteId', formData.slicerQuoteId);
      data.append('targetMachineId', formData.targetMachineId);
      data.append('sequenceMode', formData.sequenceMode);
      if (formData.reslice) data.append('reslice', 'true');
      if (formData.instructions) data.append('instructions', formData.instructions);
      if (file) data.append('file', file);

      await api.post('/auth/print-queue/inject', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to inject job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Inject Quote into Print Queue</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slicer Quote *</label>
            {quotesLoading ? (
              <div className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-400">Loading paid quotes...</div>
            ) : paidQuotes.length > 0 ? (
              <select
                value={formData.slicerQuoteId}
                onChange={(e) => setFormData({ ...formData, slicerQuoteId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                required
              >
                <option value="">Select a paid quote...</option>
                {paidQuotes.map((q) => (
                  <option key={q._id} value={q._id}>
                    {q.contactName || q.contactEmail || 'Unknown'} — {q.file?.name || 'No file'} ({q.step || '-'})
                  </option>
                ))}
              </select>
            ) : (
              <div>
                <input
                  type="text"
                  value={formData.slicerQuoteId}
                  onChange={(e) => setFormData({ ...formData, slicerQuoteId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="No paid quotes found — enter ID manually"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">No PAID quotes available. You can paste a quote ID directly.</p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Target Machine *</label>
            <select
              value={formData.targetMachineId}
              onChange={(e) => setFormData({ ...formData, targetMachineId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              required
            >
              <option value="">Select a machine...</option>
              {machines.map((m) => (
                <option key={m._id} value={m._id}>
                  {m.name} ({m.uid})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sequence Mode *</label>
            <select
              value={formData.sequenceMode}
              onChange={(e) => setFormData({ ...formData, sequenceMode: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="BATCH">Batch (by plate)</option>
              <option value="KIT">Kit (by unit)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Batch: complete all units of one plate before moving to the next. Kit: complete one unit of every plate before repeating.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Production 3MF File</label>
            <input
              type="file"
              accept=".3mf"
              onChange={(e) => setFile(e.target.files[0] || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.reslice}
              onChange={(e) => setFormData({ ...formData, reslice: e.target.checked })}
              className="h-4 w-4 text-blue-600 rounded"
            />
            <span className="text-sm text-gray-700">Re-slice uploaded 3MF for accurate print times</span>
          </label>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
            <textarea
              value={formData.instructions}
              onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
              maxLength={2000}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="Operator notes..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center"
            >
              {loading && <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />}
              {loading ? 'Injecting...' : 'Inject into Queue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
